/**
 * ModalCloseButton — top-right X for the various ad-hoc modal/sheet
 * panels scattered across the app.
 *
 * Why this exists:
 * The two shared `Modal` components (in GamePage.jsx and SchedulePage.jsx)
 * both got a sticky header with a close X built into them in v2.5.45.
 * The other ~9 modals in the app are inlined per-page with their own
 * panel structure (Message Players, Email Players, Add Player, FCFS
 * Window, Rename Home/Away, Roster Swap, etc.). Rather than rebuild
 * each one's header, this drops a single absolute-positioned X button
 * into each panel's top-right corner.
 *
 * Usage:
 *   <div style={{ position: 'relative', ...panelStyles }}>
 *     <ModalCloseButton onClose={() => setOpen(false)} />
 *     ...rest of modal content
 *   </div>
 *
 * Notes for the caller:
 *   - The parent panel MUST be `position: relative`.
 *   - For scrollable panels (overflow-y: auto), the X scrolls with
 *     content. That's an acceptable trade-off here — backdrop tap
 *     and inline Cancel buttons are still available, and a sticky
 *     pattern would require restructuring each panel's children.
 *     The shared Modal components handle long lists with a sticky
 *     header instead.
 *   - When the modal is mid-action (sending, saving), pass disabled
 *     so the X greys out and tap is a no-op — same pattern as the
 *     existing backdrop-dismiss guards.
 *   - If the heading sits at the top-left of the panel, give it
 *     `paddingRight: 44` (or className `pr-11`) so it doesn't
 *     visually collide with the button. Centered headings don't
 *     need this — the X sits in negative space.
 *
 * — May 2 2026
 */
import { X } from 'lucide-react';

export default function ModalCloseButton({ onClose, disabled = false, style = {} }) {
  return (
    <button
      type="button"
      onClick={() => { if (!disabled) onClose(); }}
      disabled={disabled}
      aria-label="Close"
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 2,
        width: 36,
        height: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid var(--border)',
        color: 'var(--text-2)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        opacity: disabled ? 0.4 : 1,
        WebkitTapHighlightColor: 'transparent',
        transition: 'opacity 0.15s, background 0.15s',
        ...style,
      }}>
      <X size={18} />
    </button>
  );
}
